/**
 * People, drawn: overlapping avatars with a hover card behind each face.
 *
 * The avatar is the workspace's own (`ui/avatar`, the treatment the sidebar
 * account chip uses) and the hover surface is the workspace's own
 * (`ui/hover-card`, Base UI's PreviewCard) — a face here and a face there
 * should be the same face.
 *
 * @module components/regulation/PersonAvatars
 */
import { cn } from "~/lib/utils";
import type { OrganizationPerson } from "../../state/governance";
import { Avatar, AvatarFallback, AvatarImage, personInitials } from "../ui/avatar";
import { HoverCard, HoverCardPopup, HoverCardTrigger } from "../ui/hover-card";

const SIZES = {
  sm: { avatar: "size-6 text-[10px]", overlap: "-ml-1.5" },
  md: { avatar: "size-8 text-[11px]", overlap: "-ml-2" },
} as const;

function PersonAvatar({
  person,
  size,
  ring = true,
}: {
  readonly person: OrganizationPerson;
  readonly size: keyof typeof SIZES;
  readonly ring?: boolean;
}) {
  return (
    <Avatar className={cn(SIZES[size].avatar, ring && "ring-2 ring-background")}>
      {person.avatarUrl ? (
        <AvatarImage src={person.avatarUrl} />
      ) : (
        <AvatarFallback>{personInitials(person)}</AvatarFallback>
      )}
    </Avatar>
  );
}

/** Who they are, in the hover surface: name, standing here, and how to reach them. */
export function PersonCard({ person }: { readonly person: OrganizationPerson }) {
  return (
    <div className="flex items-start gap-3">
      <PersonAvatar person={person} size="md" ring={false} />
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-sm font-medium text-foreground">
          <span className="truncate">{person.name ?? person.email}</span>
          {person.role === "admin" ? (
            <span className="shrink-0 rounded-full bg-muted px-1.5 py-px text-[10px] font-medium text-muted-foreground">
              Admin
            </span>
          ) : null}
        </p>
        {person.jobTitle || person.department ? (
          <p className="mt-0.5 truncate text-xs text-foreground/80">
            {[person.jobTitle, person.department].filter(Boolean).join(" · ")}
          </p>
        ) : (
          <p className="mt-0.5 text-xs text-muted-foreground">
            No role recorded for this organization
          </p>
        )}
        <p className="mt-1 truncate text-xs text-muted-foreground">{person.email}</p>
        {person.responsibility ? (
          <p className="mt-2 border-t border-border/60 pt-2 text-xs text-foreground">
            {person.responsibility}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function PersonAvatars({
  people,
  size = "sm",
  max = 3,
  emptyLabel = "Unassigned",
  className,
}: {
  readonly people: ReadonlyArray<OrganizationPerson>;
  readonly size?: keyof typeof SIZES;
  readonly max?: number;
  readonly emptyLabel?: string;
  readonly className?: string;
}) {
  if (people.length === 0) {
    return <span className={cn("text-xs text-muted-foreground", className)}>{emptyLabel}</span>;
  }
  const shown = people.slice(0, max);
  const overflow = people.slice(max);

  return (
    <span className={cn("flex items-center", className)}>
      {shown.map((person, index) => (
        <HoverCard key={person.userId}>
          <HoverCardTrigger
            render={
              <span
                tabIndex={0}
                aria-label={person.name ?? person.email}
                className={cn(
                  "cursor-default rounded-full transition-transform hover:z-10 hover:-translate-y-0.5",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                  index > 0 && SIZES[size].overlap,
                )}
              />
            }
          >
            <PersonAvatar person={person} size={size} />
          </HoverCardTrigger>
          <HoverCardPopup className="w-72">
            <PersonCard person={person} />
          </HoverCardPopup>
        </HoverCard>
      ))}
      {overflow.length > 0 ? (
        <HoverCard>
          <HoverCardTrigger
            render={
              <span
                tabIndex={0}
                className={cn(
                  "flex cursor-default items-center justify-center rounded-full bg-muted font-medium text-muted-foreground ring-2 ring-background",
                  SIZES[size].avatar,
                  SIZES[size].overlap,
                )}
              />
            }
          >
            +{overflow.length}
          </HoverCardTrigger>
          <HoverCardPopup className="w-72">
            <ul className="space-y-3">
              {overflow.map((person) => (
                <li key={person.userId}>
                  <PersonCard person={person} />
                </li>
              ))}
            </ul>
          </HoverCardPopup>
        </HoverCard>
      ) : null}
    </span>
  );
}
